<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import {PhTrendUp, PhTrendDown, PhMinus} from '@phosphor-icons/vue'
import type { Component } from 'vue'

interface Props {
  label: string
  value: string | number
  trend?: number
  trendLabel?: string
  icon?: Component
  accent?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  trend: 0,
  accent: 'primary',
  loading: false,
})

const displayValue = ref(props.value)
const isAnimating = ref(false)

const accentClasses = {
  primary: 'text-accent-cyan',
  success: 'text-accent-emerald',
  warning: 'text-accent-amber',
  danger: 'text-accent-red',
  info: 'text-accent-blue',
}

const accentBgClasses = {
  primary: 'bg-cyan-500/10 border-cyan-500/20',
  success: 'bg-emerald-500/10 border-emerald-500/20',
  warning: 'bg-amber-500/10 border-amber-500/20',
  danger: 'bg-red-500/10 border-red-500/20',
  info: 'bg-blue-500/10 border-blue-500/20',
}

const trendIcon = computed(() => {
  if (props.trend > 0) return PhTrendUp
  if (props.trend < 0) return PhMinus
  return PhTrendDown
})

const trendColor = computed(() => {
  if (props.trend > 0) return 'text-accent-emerald'
  if (props.trend < 0) return 'text-accent-red'
  return 'text-ink-6'
})

function animateValue() {
  if (isAnimating.value || typeof props.value !== 'number') return

  isAnimating.value = true
  const target = props.value
  const duration = 800
  const start = 0
  const startTime = performance.now()

  function update(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const eased = 1 - Math.pow(1 - progress, 3)
    displayValue.value = Math.round(start + (target - start) * eased)

    if (progress < 1) {
      requestAnimationFrame(update)
    } else {
      displayValue.value = target
      isAnimating.value = false
    }
  }

  requestAnimationFrame(update)
}

onMounted(() => {
  animateValue()
})

const formattedValue = computed(() => {
  if (typeof props.value === 'number') {
    return displayValue.value.toLocaleString()
  }
  return props.value
})
</script>

<template>
  <div
    class="card rounded-xl border border-line-2 bg-surface/50 backdrop-blur-sm transition-all duration-300 hover:border-line-3 hover:bg-surface"
    :class="accentBgClasses[props.accent]"
  >
    <div class="p-5">
      <div class="flex items-start justify-between gap-4">
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-ink-5 uppercase tracking-wider mb-1.5">
            {{ props.label }}
          </p>
          <p class="text-3xl font-bold text-ink-2 tabular-nums">
            {{ formattedValue }}
          </p>

          <div v-if="props.trend !== 0" class="flex items-center gap-1.5 mt-2">
            <component
              :is="trendIcon"
              class="w-4 h-4"
              :class="trendColor"
            />
            <span class="text-sm font-medium" :class="trendColor">
              {{ props.trend > 0 ? '+' : '' }}{{ props.trend }}%
            </span>
            <span v-if="props.trendLabel" class="text-xs text-ink-6">
              {{ props.trendLabel }}
            </span>
          </div>
        </div>

        <div v-if="props.icon" class="shrink-0">
          <div
            class="w-12 h-12 rounded-xl flex items-center justify-center"
            :class="accentClasses[props.accent]"
          >
            <component :is="props.icon" class="w-6 h-6" />
          </div>
        </div>
      </div>

      <div v-if="props.loading" class="mt-4 h-1.5 bg-surface-2 rounded-full overflow-hidden">
        <div
          class="h-full bg-linear-to-r from-transparent via-current to-transparent animate-shimmer"
          style="width: 60%"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
}

.animate-shimmer {
  animation: shimmer 1.5s infinite;
}
</style>